/**
 * @fileoverview The share buttons every sharer in Grow uses: Share, Copy link,
 * and for a link on its own, the row that holds them under the link.
 *
 * The owner's rule (2026-10-09): every sharer works the same. A picture or a
 * page is shared from the share sheet (SharePlantDialog), whose bar is made
 * of these buttons beside Save picture and Copy picture; a link on its own
 * (the garden page's address in Profile > Public pages, a gift's redeem link)
 * is shared from LinkShareRow, the same buttons with the same words, the
 * link above them and the privacy line, as in the sheet, just before them.
 *
 * Words are one set for all (share.actions.*): "Share", "Save picture",
 * "Copy picture", "Copy link", "Copied", and the two sentences said when the
 * browser would not share or copy. Neither is a failure on our side (nothing
 * is asked of the server), so neither goes to Sentry; each says what to do
 * instead, with the link written out so it can be selected by hand.
 *
 * Where: SharePlantDialog's bar (ShareActionButton, CopyLinkButton),
 * GardenPageRow and GiftThanksPage (LinkShareRow).
 * Shared with it: lib/shareLink (SHARE_BUTTON_CLASS, canShareLink).
 * Settings: COPIED_RESET_MS (config/tunables/ui), how long "Copied" shows.
 * Words (en/es): share.actions.*.
 *
 * @module ShareButtons
 */

import React from 'react';
import { Check, Link2, Share2, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { COPIED_RESET_MS } from '@/config/tunables/ui';
import { SHARE_BUTTON_CLASS, canShareLink } from './lib/shareLink';

/**
 * "Copied" for a moment after a copy (COPIED_RESET_MS), then the button's
 * own word again; the tick goes too when what it copies changes.
 *
 * @param resetOn - What the button copies: a new one clears the tick.
 * @returns Whether the tick shows, and the setter that shows it.
 */
function useCopiedTick(resetOn: string): [boolean, (on: boolean) => void] {
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => { setCopied(false); }, [resetOn]);
  React.useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), COPIED_RESET_MS);
    return () => clearTimeout(timer);
  }, [copied]);
  return [copied, setCopied];
}

/** What Copy link needs. */
interface CopyLinkButtonProps {
  /** The address copied. */
  link: string;
  /** Told on every press, before the copy: the share sheet records the link's use. */
  onUse?: () => void;
  /** Told when the browser would not copy, with the sentence to show. */
  onError?: (message: string | null) => void;
  /** The button's look: outline beside a Share, filled when it is the only way. */
  variant?: 'default' | 'outline';
  /** Extra classes, for the bar's grid. */
  className?: string;
}

/**
 * Copy link: the address on the clipboard, and "Copied" with a tick for a
 * moment. Without a clipboard, or when the browser refuses, nothing was
 * copied, so it never says it was; the sentence it hands onError gives the
 * address to select by hand.
 *
 * @param props - CopyLinkButtonProps.
 */
export const CopyLinkButton: React.FC<CopyLinkButtonProps> = ({ link, onUse, onError, variant = 'outline', className }) => {
  const { reactTranslate: t } = useTranslation();
  const [copied, setCopied] = useCopiedTick(link);
  /** Copies the link, and says so, or why not. */
  const copy = async (): Promise<void> => {
    onUse?.();
    onError?.(null);
    try {
      if (!navigator.clipboard) throw new Error('No clipboard');
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      onError?.(t('share.actions.copyLinkFailed', { link: link.replace(/^https?:\/\//, '') }));
    }
  };
  return (
    <Button
      type="button"
      variant={variant}
      className={cn(SHARE_BUTTON_CLASS, className)}
      onClick={() => void copy()}
      title={link.replace(/^https?:\/\//, '')}
      aria-live="polite"
    >
      {copied ? <Check className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" /> : <Link2 className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />}
      {copied ? t('share.actions.copied') : t('share.actions.copyLink')}
    </Button>
  );
};

/** What a Share button for a link needs. */
interface ShareLinkButtonProps {
  /** The address shared. */
  link: string;
  /** The share's title, for the apps that show one. */
  title: string;
  /** The words that go before the link. */
  text?: string;
  /** Told when the browser would not share, with the sentence to show. */
  onError?: (message: string | null) => void;
  /** Extra classes, for the row. */
  className?: string;
}

/**
 * Share, for a link on its own: the browser's share menu with the title, the
 * words and the link. Only drawn where there is a share menu (canShareLink).
 * Closing the menu without choosing says nothing.
 *
 * @param props - ShareLinkButtonProps.
 */
export const ShareLinkButton: React.FC<ShareLinkButtonProps> = ({ link, title, text, onError, className }) => {
  const { reactTranslate: t } = useTranslation();
  if (!canShareLink()) return null;
  /** Opens the share menu. */
  const share = async (): Promise<void> => {
    onError?.(null);
    try {
      await navigator.share({ title, text, url: link });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      onError?.(t('share.actions.shareLinkFailed'));
    }
  };
  return <ShareActionButton className={className} onClick={() => void share()} />;
};

/** What the Share button itself needs. */
interface ShareActionButtonProps {
  /** What a press does. */
  onClick: () => void;
  /** Whether it waits for something (the pictures being made). */
  disabled?: boolean;
  /** Extra classes, for the bar's grid. */
  className?: string;
}

/**
 * The Share button, filled, with the share icon and "Share": the one look
 * for every share in Grow, of pictures or of a link.
 *
 * @param props - ShareActionButtonProps.
 */
export const ShareActionButton: React.FC<ShareActionButtonProps> = ({ onClick, disabled, className }) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <Button type="button" className={cn(SHARE_BUTTON_CLASS, className)} onClick={onClick} disabled={disabled}>
      <Share2 className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
      {t('share.actions.share')}
    </Button>
  );
};

/**
 * A sentence under the share buttons when the browser would not share or
 * copy, in the sheet's red with its cross.
 *
 * @param props.text - The sentence.
 */
export const ShareError: React.FC<{ text: React.ReactNode }> = ({ text }) => (
  <p className="flex items-center gap-1.5 text-sm text-destructive" role="alert">
    <X className="h-4 w-4 shrink-0" aria-hidden="true" /><span className="break-all">{text}</span>
  </p>
);

/** What a link on its own needs to be shared. */
interface LinkShareRowProps {
  /** The address. */
  link: string;
  /** The share's title, for the apps that show one. */
  title: string;
  /** The words that go before the link in a share. */
  text?: string;
  /** The privacy line, said just before the buttons as in the share sheet. */
  note?: React.ReactNode;
  /** Other buttons of the row, after Share and Copy link (the garden page's Change). */
  children?: React.ReactNode;
}

/**
 * A link on its own, shared the way the share sheet shares: the address
 * (which opens in a new tab), the privacy line, then Share (where the
 * browser has a share menu) and Copy link, in the sheet's sizes and words.
 *
 * @param props - LinkShareRowProps.
 */
export const LinkShareRow: React.FC<LinkShareRowProps> = ({ link, title, text, note, children }) => {
  const [error, setError] = React.useState<string | null>(null);
  const sheet = canShareLink();
  return (
    <div className="space-y-2">
      <a href={link} target="_blank" rel="noopener noreferrer" className="block break-all text-sm underline decoration-1 underline-offset-4 hover:decoration-2">
        {link.replace(/^https?:\/\//, '')}
      </a>
      {note && <p className="text-xs text-muted-foreground">{note}</p>}
      <div className="flex flex-wrap gap-2">
        <ShareLinkButton link={link} title={title} text={text} onError={setError} className="flex-1 sm:flex-none" />
        <CopyLinkButton link={link} onError={setError} variant={sheet ? 'outline' : 'default'} className="flex-1 sm:flex-none" />
        {children}
      </div>
      {error && <ShareError text={error} />}
    </div>
  );
};
